import { useEffect, useRef, useState } from "react";
import { ClickAwayListener, Popper } from "@mui/material";
import { useForm } from "react-hook-form";
import Button from "@/components/Button/Button";
import Modal from "@/components/Modal/Modal";
import { PencilMiniIcon } from "@/components/SVGComponents/MemberIcons";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  PROFILE_IMAGE_ACCEPT,
  validateProfileImageFile,
} from "@/lib/profileImage";
import {
  addBaseUrl,
  captalizeEachWord,
  getCurrentUser,
  inputDateFormat,
  saveCurrentUser,
} from "@/lib/utils";
import toast from "@/lib/toast";
import {
  updateUserProfile,
  updateUserProfileImage,
  type UpdateUserPayload,
  type UserProfile,
} from "@/services/api/userProfile";
import styles from "./EditProfileModal.module.scss";

interface EditProfileModalProps {
  open: boolean;
  onClose: () => void;
  onUpdated?: () => Promise<void> | void;
  profile: UserProfile | null;
  notificationsEnabled: boolean;
}

interface EditProfileFormValues {
  firstName: string;
  lastName: string;
  dob: string;
  companyName: string;
  linkedinProfileUrl: string;
}

const TODAY = new Date().toISOString().split("T")[0] || "";

const getDefaultValues = (
  profile: UserProfile | null,
): EditProfileFormValues => ({
  firstName: profile?.firstName || "",
  lastName: profile?.lastName || "",
  dob: inputDateFormat(profile?.dob || "") || "",
  companyName: profile?.companyName || "",
  linkedinProfileUrl: profile?.socialLink?.linkedinProfileUrl || "",
});

const getNullableValue = (value: string) => {
  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : null;
};

const getInitials = (firstName?: string, lastName?: string) =>
  [firstName, lastName]
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part?.[0]?.toUpperCase())
    .join("");

const EditProfileModal = ({
  open,
  onClose,
  onUpdated,
  profile,
  notificationsEnabled,
}: EditProfileModalProps) => {
  const { selectedLanguage, t } = useLanguage();
  const currentUser = getCurrentUser() || {};
  const emailAddress = profile?.email || currentUser?.email || "";
  const isInvestor = profile?.userType === "INVESTOR";
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const editButtonRef = useRef<HTMLButtonElement | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const [currentProfileImage, setCurrentProfileImage] = useState<string | null>(
    null,
  );
  const [selectedProfileFile, setSelectedProfileFile] = useState<File | null>(
    null,
  );
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isImageDeleted, setIsImageDeleted] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isImageMenuOpen, setIsImageMenuOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<EditProfileFormValues>({
    defaultValues: getDefaultValues(profile),
  });

  const watchedFirstName = watch("firstName");
  const watchedLastName = watch("lastName");

  const resetImageState = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }

    setCurrentProfileImage(
      profile?.profileUrl ? addBaseUrl(profile.profileUrl) : null,
    );
    setSelectedProfileFile(null);
    setImagePreview(null);
    setIsImageDeleted(false);
    setImageError(null);
    setIsImageMenuOpen(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (open) {
      reset(getDefaultValues(profile));
      resetImageState();
    }
  }, [open, profile, reset]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const hasImageChanges = Boolean(selectedProfileFile) || isImageDeleted;
  const hasExistingProfileImage = Boolean(currentProfileImage) && !isImageDeleted;
  const showDeletePhotoOption =
    hasExistingProfileImage || Boolean(selectedProfileFile);

  const getDisplayAvatarUrl = () => {
    if (imagePreview) {
      return imagePreview;
    }

    if (isImageDeleted || !currentProfileImage) {
      return null;
    }

    return currentProfileImage;
  };

  const displayAvatarUrl = getDisplayAvatarUrl();
  const avatarInitials =
    getInitials(watchedFirstName, watchedLastName) ||
    getInitials(profile?.firstName, profile?.lastName) ||
    "JW";

  const closeImageMenu = () => {
    setIsImageMenuOpen(false);
  };

  const handleUploadPhotoClick = () => {
    closeImageMenu();
    fileInputRef.current?.click();
  };

  const handleDeletePhotoClick = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }

    setSelectedProfileFile(null);
    setImagePreview(null);
    setIsImageDeleted(true);
    setImageError(null);
    closeImageMenu();

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleProfileImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const validationError = validateProfileImageFile(file);

    if (validationError) {
      setImageError(validationError);
      event.target.value = "";
      return;
    }

    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    previewUrlRef.current = nextPreviewUrl;
    setSelectedProfileFile(file);
    setImagePreview(nextPreviewUrl);
    setIsImageDeleted(false);
    setImageError(null);
  };

  const submitForm = async (values: EditProfileFormValues) => {
    if (imageError) {
      return;
    }

    const preferredLanguage =
      selectedLanguage?.languageCode ||
      profile?.userSettings?.language ||
      currentUser?.userSettings?.language ||
      currentUser?.language ||
      "en";
    const linkedinProfileUrl = getNullableValue(values.linkedinProfileUrl);
    const payload: UpdateUserPayload = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      companyName: getNullableValue(values.companyName),
      dob: values.dob || null,
      socialLink: linkedinProfileUrl
        ? {
            linkedinProfileUrl,
            instagramProfileUrl:
              profile?.socialLink?.instagramProfileUrl ?? null,
          }
        : undefined,
    };

    const response = await toast.promise(
      async () => {
        let updatedProfile = profile;

        if (isDirty) {
          const profileResponse = await updateUserProfile(payload);
          updatedProfile = profileResponse.data;
        }

        if (hasImageChanges) {
          const imageFile = selectedProfileFile || null;
          const imageResponse = await updateUserProfileImage(imageFile);
          updatedProfile = imageResponse.data;
        }

        return updatedProfile;
      },
      {
        loading: t(
          ["profile.edit.loading", "Updating your profile..."],
          "Updating your profile...",
        ),
        success: t(
          ["profile.edit.success", "Profile updated successfully"],
          "Profile updated successfully",
        ),
        error: (error) =>
          error?.response?.data?.message ||
          error?.message ||
          t(
            ["profile.edit.error", "Failed to update your profile"],
            "Failed to update your profile",
          ),
      },
    );

    const updatedProfile = response;
    const nextDisplayName = captalizeEachWord(
      [updatedProfile?.firstName, updatedProfile?.lastName]
        .filter(Boolean)
        .join(" "),
    );

    saveCurrentUser({
      email: updatedProfile?.email || currentUser?.email,
      firstName: updatedProfile?.firstName,
      lastName: updatedProfile?.lastName,
      location: updatedProfile?.location,
      mobileNo: updatedProfile?.mobileNo,
      name: nextDisplayName,
      userName: nextDisplayName,
      profileUrl: updatedProfile?.profileUrl ?? null,
      userType: updatedProfile?.userType || currentUser?.userType,
      language:
        updatedProfile?.userSettings?.language ||
        preferredLanguage ||
        currentUser?.language,
      userSettings: {
        ...(currentUser?.userSettings || {}),
        isPushNotificationEnabled:
          updatedProfile?.userSettings?.isPushNotificationEnabled ??
          notificationsEnabled,
        language:
          updatedProfile?.userSettings?.language ||
          preferredLanguage ||
          currentUser?.language,
      },
    });

    await onUpdated?.();
    onClose();
  };

  return (
    <Modal
      bottomSheetOnMobile
      modalOpen={open}
      closeModal={() => onClose()}
      heading={
        <div className={styles.headingRow}>
          <div>
            <div className={styles.headingTitle}>
              {t(["profile.edit.title", "Edit profile"], "Edit profile")}
            </div>
          </div>
        </div>
      }
      className={styles.modalShell}
    >
      <form className={styles.form} onSubmit={handleSubmit(submitForm)} noValidate>
        <div className={styles.avatarSection}>
          <div className={styles.avatarWrap}>
            <div
              className={`${styles.avatarFrame} ${
                isSubmitting ? styles.avatarFrameLoading : ""
              }`}
            >
              {displayAvatarUrl ? (
                <img
                  alt={t(
                    ["profile.edit.avatarAlt", "Profile photo"],
                    "Profile photo",
                  )}
                  className={styles.avatarImage}
                  src={displayAvatarUrl}
                />
              ) : (
                <div className={styles.avatarFallback}>{avatarInitials}</div>
              )}

              {isSubmitting ? (
                <div aria-hidden className={styles.avatarLoader} />
              ) : null}
            </div>

            <button
              ref={editButtonRef}
              aria-expanded={isImageMenuOpen}
              aria-haspopup="menu"
              aria-label={t(
                ["profile.edit.changePhoto", "Change profile photo"],
                "Change profile photo",
              )}
              className={styles.editBadge}
              disabled={isSubmitting}
              onClick={() => setIsImageMenuOpen((previous) => !previous)}
              type="button"
            >
              <PencilMiniIcon />
            </button>

            <input
              ref={fileInputRef}
              accept={PROFILE_IMAGE_ACCEPT}
              className={styles.hiddenFileInput}
              onChange={handleProfileImageChange}
              tabIndex={-1}
              type="file"
            />

            <Popper
              anchorEl={editButtonRef.current}
              className={styles.imageMenuPopper}
              disablePortal={false}
              modifiers={[
                {
                  name: "offset",
                  options: { offset: [0, 8] },
                },
              ]}
              open={isImageMenuOpen}
              placement="bottom"
            >
              <ClickAwayListener onClickAway={closeImageMenu}>
                <div className={styles.imageMenu} role="menu">
                  <button
                    className={styles.imageMenuItem}
                    onClick={handleUploadPhotoClick}
                    role="menuitem"
                    type="button"
                  >
                    {t(
                      ["profile.edit.uploadPhoto", "Upload Photo"],
                      "Upload Photo",
                    )}
                  </button>

                  {showDeletePhotoOption ? (
                    <button
                      className={`${styles.imageMenuItem} ${styles.imageMenuItemDanger}`}
                      onClick={handleDeletePhotoClick}
                      role="menuitem"
                      type="button"
                    >
                      {t(
                        ["profile.edit.deletePhoto", "Delete Photo"],
                        "Delete Photo",
                      )}
                    </button>
                  ) : null}
                </div>
              </ClickAwayListener>
            </Popper>
          </div>

          {imageError ? (
            <small className={styles.imageError}>{imageError}</small>
          ) : null}
        </div>

        <div className={styles.grid}>
          <label className={styles.field}>
            <span>{t(["profile.fields.firstName", "First name"], "First name")}</span>
            <input
              {...register("firstName", {
                required: "First name is required",
              })}
              className={styles.input}
              placeholder="Enter your first name"
              type="text"
            />
            {errors.firstName ? (
              <small>{errors.firstName.message}</small>
            ) : null}
          </label>

          <label className={styles.field}>
            <span>{t(["profile.fields.lastName", "Last name"], "Last name")}</span>
            <input
              {...register("lastName", {
                required: "Last name is required",
              })}
              className={styles.input}
              placeholder="Enter your last name"
              type="text"
            />
            {errors.lastName ? <small>{errors.lastName.message}</small> : null}
          </label>

          <label className={`${styles.field} ${styles.fieldFullWidth}`}>
            <span>{t(["profile.fields.email", "Email"], "Email")}</span>
            <input
              className={`${styles.input} ${styles.inputDisabled}`}
              disabled
              type="email"
              value={emailAddress}
              readOnly
            />
          </label>

          <label className={styles.field}>
            <span>{t(["profile.fields.dob", "Date of birth"], "Date of birth")}</span>
            <input
              {...register("dob")}
              className={styles.input}
              max={TODAY}
              type="date"
            />
          </label>

          <label className={styles.field}>
            <span>
              {isInvestor
                ? t(["profile.fields.firm", "Firm name"], "Firm name")
                : t(["profile.fields.company", "Company name"], "Company name")}
            </span>
            <input
              {...register("companyName")}
              className={styles.input}
              placeholder={
                isInvestor ? "Enter your firm name" : "Enter your company name"
              }
              type="text"
            />
          </label>

          <label className={`${styles.field} ${styles.fieldFullWidth}`}>
            <span>{t(["profile.fields.linkedin", "LinkedIn URL"], "LinkedIn URL")}</span>
            <input
              {...register("linkedinProfileUrl")}
              className={styles.input}
              placeholder="https://linkedin.com/in/username"
              type="url"
            />
          </label>
        </div>

        <div className={styles.actions}>
          <Button
            className={styles.secondaryAction}
            onClick={onClose}
            outlined
            type="button"
          >
            {t(["common.cancel", "Cancel"], "Cancel")}
          </Button>

          <Button
            className={styles.primaryAction}
            disabled={isSubmitting || (!isDirty && !hasImageChanges) || Boolean(imageError)}
            type="submit"
          >
            {isSubmitting
              ? t(["profile.edit.saving", "Updating..."], "Updating...")
              : t(["profile.edit.update", "Update"], "Update")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditProfileModal;
